import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import { format, parseISO } from 'date-fns';

const { FiStar, FiMessageSquare, FiTrendingUp, FiAlertCircle, FiCheckCircle, FiSearch, FiFilter, FiSmile, FiMeh, FiFrown } = FiIcons;

const Feedback = () => {
  const { data } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const feedbackList = (data.feedback || [])
    .filter(f => {
      const tenant = data.tenants.find(t => t.id === f.tenantId);
      const property = data.properties.find(p => p.id === f.propertyId);
      const matchesSearch = 
        tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.comment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || f.type.toLowerCase() === filterType.toLowerCase();
      return matchesSearch && matchesType;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const avgRating = (data.feedback?.reduce((acc, f) => acc + f.rating, 0) / (data.feedback?.length || 1)).toFixed(1);

  const sentimentData = [
    { value: data.feedback?.filter(f => f.sentiment === 'Positive').length, name: 'Positive' },
    { value: data.feedback?.filter(f => f.sentiment === 'Neutral').length, name: 'Neutral' },
    { value: data.feedback?.filter(f => f.sentiment === 'Negative').length, name: 'Negative' }
  ];

  const sentimentOption = {
    tooltip: { trigger: 'item' },
    series: [{
      type: 'pie',
      radius: ['60%', '80%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      data: sentimentData,
      color: ['#10B981', '#F59E0B', '#EF4444']
    }]
  };

  const getSentimentIcon = (sentiment) => {
    if (sentiment === 'Positive') return <SafeIcon icon={FiSmile} className="text-green-500" />;
    if (sentiment === 'Neutral') return <SafeIcon icon={FiMeh} className="text-amber-500" />;
    return <SafeIcon icon={FiFrown} className="text-red-500" />;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Feedback Loop</h2>
          <p className="text-sm text-gray-500 font-medium">Monitoring guest satisfaction and stay health.</p>
        </div>
        <button className="flex items-center px-5 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider">
          <SafeIcon icon={FiMessageSquare} className="mr-2" /> Send New Survey
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Portfolio Sat Score</p>
          <div className="flex items-center gap-2">
            <span className="text-4xl font-black text-gray-900">{avgRating}</span>
            <SafeIcon icon={FiStar} className="text-yellow-400 fill-current" size={24} />
          </div>
          <p className="text-[10px] text-green-600 font-bold mt-2 uppercase tracking-tighter">+0.3 from last month</p>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col items-center justify-center">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Sentiment Split</p>
          <div className="w-full h-24">
            <ReactECharts option={sentimentOption} style={{ height: '100%' }} />
          </div>
        </div>

        <div className="lg:col-span-2 bg-indigo-600 p-6 rounded-3xl text-white shadow-lg overflow-hidden relative group">
          <div className="relative z-10">
            <h3 className="text-lg font-black tracking-tight mb-1">Response Rate</h3>
            <p className="text-indigo-100 text-xs font-medium mb-4">Percentage of guests completing periodic surveys.</p>
            <div className="flex items-end gap-4">
               <div>
                 <p className="text-3xl font-black">84%</p>
                 <p className="text-[10px] uppercase font-bold text-indigo-200">Industry Avg: 62%</p>
               </div>
               <div className="flex-1 bg-white/20 h-1.5 rounded-full mb-2">
                  <div className="bg-white h-full rounded-full w-[84%]" />
               </div>
            </div>
          </div>
          <SafeIcon icon={FiTrendingUp} className="absolute -bottom-4 -right-4 text-white/10" size={120} />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search responses, tenants or properties..." 
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
           <SafeIcon icon={FiFilter} className="text-gray-400" />
           <select 
             className="bg-gray-50 border-none rounded-2xl px-4 py-3 text-sm font-bold text-gray-600 outline-none focus:ring-2 focus:ring-indigo-500"
             value={filterType}
             onChange={e => setFilterType(e.target.value)}
           >
             <option value="all">All Journey Points</option>
             <option value="Check-in">Check-in</option>
             <option value="Mid-stay">Mid-stay (30 Day)</option>
             <option value="Check-out">Check-out</option>
           </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {feedbackList.map((f) => {
          const tenant = data.tenants.find(t => t.id === f.tenantId);
          const property = data.properties.find(p => p.id === f.propertyId);
          
          return (
            <div key={f.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-all group">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center font-black text-xs">
                    {tenant?.name[0]}
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 text-sm">{tenant?.name}</h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase">{property?.name}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} size={12} className={i < f.rating ? "fill-current" : "text-gray-200"} />
                    ))}
                  </div>
                  <span className="text-[9px] text-gray-400 font-bold mt-1 uppercase">{format(parseISO(f.date), 'MMM dd, yyyy')}</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-4 mb-4 relative">
                <div className="absolute -top-2 -left-2">
                  {getSentimentIcon(f.sentiment)}
                </div>
                <p className="text-sm text-gray-700 font-medium italic leading-relaxed">"{f.comment}"</p>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-white border border-gray-100 rounded-lg text-[9px] font-black text-gray-500 uppercase tracking-wider">
                    {f.type}
                  </span>
                  <span className="px-2 py-1 bg-white border border-gray-100 rounded-lg text-[9px] font-black text-indigo-600 uppercase tracking-wider">
                    {f.category}
                  </span>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                   <button className="text-[10px] font-black text-indigo-600 uppercase hover:underline flex items-center gap-1">
                      <SafeIcon icon={FiCheckCircle} size={12} /> Resolve
                   </button>
                   <button className="text-[10px] font-black text-gray-400 uppercase hover:text-indigo-600 flex items-center gap-1">
                      <SafeIcon icon={FiMessageSquare} size={12} /> Reply
                   </button>
                </div>
              </div>
            </div>
          );
        })}
        {feedbackList.length === 0 && (
          <div className="col-span-full py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
            <SafeIcon icon={FiMeh} size={48} className="text-gray-200 mx-auto mb-4" />
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No feedback matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Feedback;